import { useState, useEffect } from 'react'
import { Phone, ArrowRight, CheckCircle, Shield, Clock, Users, MapPin, Sparkles } from 'lucide-react'

/**
 * Inline lead capture block — embeds directly in content.
 * Minimal friction: phone + postal code = lead created.
 * Use on all programmatic SEO pages for maximum conversion.
 */

interface LeadCaptureBlockProps {
  serviceName: string
  cityName?: string
  categorySlug?: string
  variant?: 'default' | 'urgent' | 'compact'
}

export function LeadCaptureBlock({ serviceName, cityName, categorySlug, variant = 'default' }: LeadCaptureBlockProps) {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)

    const form = new FormData(e.currentTarget)
    try {
      const res = await fetch('/api/quick-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: form.get('phone'),
          postalCode: form.get('postalCode'),
          service: categorySlug ?? serviceName,
          city: cityName ?? '',
          source: 'seo_page_inline',
        }),
      })
      if (res.ok) setSubmitted(true)
    } catch {}
    setLoading(false)
  }

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <CheckCircle className="mx-auto h-10 w-10 text-emerald-600" />
        <h3 className="mt-3 text-lg font-bold text-emerald-900">Demande envoyée !</h3>
        <p className="mt-2 text-sm text-emerald-700">
          Nous recherchons les meilleurs artisans {cityName ? `à ${cityName}` : 'près de chez vous'}.
          Vous serez contacté sous <strong>48 heures</strong>.
        </p>
      </div>
    )
  }

  if (variant === 'urgent') {
    return (
      <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-orange-50 p-6">
        <div className="flex items-center gap-2 text-red-700">
          <Phone className="h-5 w-5" />
          <h3 className="text-lg font-bold">Besoin urgent ?</h3>
        </div>
        <p className="mt-2 text-sm text-red-600">
          Laissez votre numéro — un artisan disponible 24/7 vous rappelle.
        </p>
        <p className="mt-1 text-xs text-red-500">Rappel sous 30 minutes</p>
        <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
          <input name="phone" type="tel" required placeholder="06 12 34 56 78"
            className="flex-1 rounded-xl border border-red-200 px-4 py-3 text-sm placeholder:text-red-300 focus:border-red-400 focus:outline-none" />
          <input name="postalCode" type="text" required maxLength={5} pattern="[0-9]{5}" placeholder="Code postal"
            className="w-28 rounded-xl border border-red-200 px-3 py-3 text-sm placeholder:text-red-300 focus:border-red-400 focus:outline-none" />
          <button type="submit" disabled={loading}
            className="rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
            {loading ? '...' : 'Rappel'}
          </button>
        </form>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
        <p className="text-sm font-semibold text-slate-900">
          Devis gratuit {serviceName.toLowerCase()} {cityName ? `à ${cityName}` : ''}
        </p>
        <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
          <input name="phone" type="tel" required placeholder="Votre téléphone"
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <input name="postalCode" type="text" required maxLength={5} pattern="[0-9]{5}" placeholder="CP"
            className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <button type="submit" disabled={loading}
            className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white hover:bg-primary/90 disabled:opacity-50">
            {loading ? '...' : <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      </div>
    )
  }

  // Default variant
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            Recevez 3 devis gratuits {cityName ? `à ${cityName}` : ''}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Comparez les prix pour {serviceName.toLowerCase()}. Réponse sous 48h, sans engagement.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input name="phone" type="tel" required placeholder="Votre numéro de téléphone"
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30" />
          </div>
          <div className="relative w-32">
            <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input name="postalCode" type="text" required maxLength={5} pattern="[0-9]{5}" placeholder="Code postal"
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-sm placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30" />
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-md shadow-primary/20 transition-all hover:bg-primary/90 hover:scale-[1.01] disabled:opacity-50">
          {loading ? 'Envoi en cours...' : (
            <>Recevoir mes devis gratuits <ArrowRight className="h-4 w-4" /></>
          )}
        </button>
      </form>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-emerald-500" /> 100% gratuit</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-blue-500" /> Réponse en 48h</span>
        <span className="flex items-center gap-1"><Users className="h-3 w-3 text-primary" /> Artisans vérifiés</span>
      </div>
    </div>
  )
}

/**
 * Callback form — ultra-minimal, phone only.
 * "Rappel gratuit" = lead with minimum friction.
 */
export function CallbackForm({ serviceName, cityName, categorySlug }: { serviceName: string; cityName?: string; categorySlug?: string }) {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    const form = new FormData(e.currentTarget)
    try {
      const res = await fetch('/api/quick-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: form.get('phone'),
          postalCode: '',
          service: categorySlug ?? serviceName,
          city: cityName ?? '',
          source: 'callback_form',
        }),
      })
      if (res.ok) setSubmitted(true)
    } catch {}
    setLoading(false)
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
        <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-700">Nous vous rappelons sous 2h !</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-blue-600" />
        <p className="text-sm font-semibold text-blue-900">Rappel gratuit en 2h</p>
      </div>
      <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
        <input name="phone" type="tel" required placeholder="Votre numéro"
          className="flex-1 rounded-lg border border-blue-200 px-3 py-2.5 text-sm placeholder:text-blue-300 focus:border-blue-400 focus:outline-none" />
        <button type="submit" disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          {loading ? '...' : 'Rappeler'}
        </button>
      </form>
      <p className="mt-2 text-[10px] text-blue-400">Gratuit · Sans engagement · Données protégées</p>
    </div>
  )
}

/**
 * Recent demands feed — shows anonymized recent leads.
 * Creates urgency and social proof.
 */
export function RecentDemands({ leads }: { leads: Array<{ city: string; categoryName: string; timeAgo: string }> }) {
  if (!leads || leads.length === 0) return null

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        Demandes récentes
      </h4>
      <div className="mt-3 space-y-2">
        {leads.slice(0, 4).map((lead, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
              {lead.city[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-700">
                {lead.categoryName} à {lead.city}
              </p>
              <p className="text-[10px] text-slate-400">il y a {lead.timeAgo}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * SEO Exit Intent — specifically for programmatic pages.
 * Shows when user tries to leave — captures phone for callback.
 */
export function SeoExitIntent({ serviceName, cityName, categorySlug }: { serviceName: string; cityName?: string; categorySlug?: string }) {
  const [visible, setVisible] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (submitted) return
    if (sessionStorage.getItem('seo-exit-shown')) return

    let handler: ((e: MouseEvent) => void) | null = null
    const timer = setTimeout(() => {
      handler = (e: MouseEvent) => {
        if (e.clientY < 5) {
          setVisible(true)
          sessionStorage.setItem('seo-exit-shown', '1')
          document.removeEventListener('mouseleave', handler!)
        }
      }
      document.addEventListener('mouseleave', handler)
    }, 10000)

    return () => {
      clearTimeout(timer)
      if (handler) document.removeEventListener('mouseleave', handler)
    }
  }, [submitted])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    const form = new FormData(e.currentTarget)
    try {
      await fetch('/api/quick-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: form.get('phone'),
          postalCode: form.get('postalCode') ?? '',
          service: categorySlug ?? serviceName,
          city: cityName ?? '',
          source: 'exit_intent',
        }),
      })
      setSubmitted(true)
    } catch {}
    setLoading(false)
  }

  if (!visible || submitted) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <button onClick={() => setVisible(false)} className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-slate-100">
          <span className="text-lg">×</span>
        </button>

        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <Phone className="h-7 w-7 text-primary" />
          </div>
          <h3 className="mt-4 text-xl font-bold text-slate-900">
            Attendez ! Votre devis est gratuit
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            Laissez juste votre numéro — on vous rappelle sous 2h avec
            les meilleurs prix pour {serviceName.toLowerCase()} {cityName ? `à ${cityName}` : ''}.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <input name="phone" type="tel" required placeholder="Votre numéro de téléphone"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-center text-lg font-medium placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          <button type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 disabled:opacity-50">
            {loading ? 'Envoi...' : 'Me rappeler gratuitement'}
          </button>
        </form>

        <p className="mt-3 text-center text-[10px] text-slate-400">
          100% gratuit · Sans engagement · Vos données sont protégées
        </p>
      </div>
    </div>
  )
}
